import { useQuery } from '@tanstack/react-query';
import { Card, SectionTitle } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { Spinner } from '@/components/LoadingScreen';
import { ErrorState } from '@/components/ErrorState';
import { integrationsService } from '@/services/integrations.service';
import { INTEGRATION_STATUS_LABELS } from '@/types/integration';
import { v } from '@/theme/tokens';

/**
 * iiko is not connected. The actions stay disabled rather than simulating a
 * connection, and no synthetic "last sync" or metric is ever displayed.
 */
export function IntegrationsSection() {
  const iiko = useQuery({
    queryKey: ['integrations', 'iiko'],
    queryFn: ({ signal }) => integrationsService.iiko(signal),
  });

  const reconciliation = useQuery({
    queryKey: ['integrations', 'iiko', 'reconciliation'],
    queryFn: ({ signal }) => integrationsService.reconciliation(undefined, undefined, signal),
  });

  const data = iiko.data;
  const connected = data?.status === 'CONNECTED';

  return (
    <Card>
      <SectionTitle>Интеграции</SectionTitle>

      {iiko.isPending && <Spinner />}
      {iiko.error && (
        <ErrorState
          message={iiko.error instanceof Error ? iiko.error.message : 'Ошибка загрузки'}
          onRetry={() => void iiko.refetch()}
        />
      )}

      {data && (
        <div
          style={{
            border: `1px solid ${v('line')}`,
            borderRadius: 14,
            padding: 18,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <div
              aria-hidden
              style={{
                width: 40,
                height: 40,
                borderRadius: 11,
                background: v('panel'),
                color: v('ink-sub'),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="plug" size={20} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>iiko</div>
              <div style={{ fontSize: 12.5, color: v('ink-muted') }}>
                Автоматическая синхронизация оперативных данных ресторана из iiko.
              </div>
            </div>
            <Badge tone={connected ? 'positive' : 'neutral'}>
              {INTEGRATION_STATUS_LABELS[data.status]}
            </Badge>
          </div>

          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))',
              gap: 10,
              margin: '14px 0',
            }}
          >
            {[
              ['Статус подключения', INTEGRATION_STATUS_LABELS[data.status]],
              [
                'Последняя синхронизация',
                data.last_sync_at ? new Date(data.last_sync_at).toLocaleString('ru-RU') : '—',
              ],
              ['Следующая синхронизация', '—'],
              ['Сопоставление объектов', data.unit_mappings.length ? String(data.unit_mappings.length) : '—'],
            ].map(([label, value]) => (
              <div key={label}>
                <dt style={{ fontSize: 11.5, color: v('ink-muted') }}>{label}</dt>
                <dd style={{ fontSize: 13.5, fontWeight: 600, margin: '2px 0 0' }}>{value}</dd>
              </div>
            ))}
          </dl>

          <div
            style={{
              background: v('panel'),
              borderRadius: 10,
              padding: '10px 12px',
              fontSize: 12.5,
              color: v('ink-sub'),
              marginBottom: 14,
            }}
          >
            Интеграция iiko пока не настроена. Подключение будет доступно после настройки
            API-доступа.
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* Disabled until real API access exists — never a fake success. */}
            <Button variant="primary" disabled title="Требуется настройка API-доступа">
              Подключить
            </Button>
            <Button disabled title="Требуется настройка API-доступа">
              Проверить соединение
            </Button>
            <Button disabled title="Требуется настройка API-доступа">
              Синхронизировать сейчас
            </Button>
          </div>

          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: v('ink-sub'), marginBottom: 6 }}>
              Журнал синхронизации
            </div>
            {data.sync_runs.length === 0 ? (
              <div style={{ fontSize: 12.5, color: v('ink-muted') }}>Синхронизаций пока не было.</div>
            ) : (
              data.sync_runs.map((run) => (
                <div key={run.id} style={{ fontSize: 12.5, padding: '5px 0' }}>
                  {new Date(run.started_at).toLocaleString('ru-RU')} — {run.status}
                </div>
              ))
            )}
          </div>

          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: v('ink-sub'), marginBottom: 6 }}>
              Сверка iiko ↔ ОПУ
            </div>
            <div style={{ fontSize: 12.5, color: v('ink-muted') }}>
              {reconciliation.data?.message ??
                'Интеграция iiko не подключена. После подключения здесь будет доступна сверка оперативной выручки iiko с данными ОПУ.'}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
